<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Icon } from '@iconify/vue'
import * as XLSX from 'xlsx-js-style'
import printJS from 'print-js'
import dayjs from 'dayjs'
import { useHTTP } from '@/plugins/http'
import { useContext } from '@/plugins/context'
import { useAlertModal } from '@/plugins/alert-modal/alert-modal'
import Location from '@/models/company/Location'
import LoadingSpinner from '@/components/LoadingSpinner.vue'

interface CashAssetRow {
  id: string
  enName: string
  arName: string
  type: 'register' | 'safe' | 'bank' | string
  locationID?: string
  locationEnName?: string
  locationArName?: string
  iqdAmount: number
  iqdPrecision: number
  usdAmount: number
  usdPrecision: number
}

interface CashAssetsTotals {
  iqdAmount: number
  iqdPrecision: number
  usdAmount: number
  usdPrecision: number
}

interface CashAssetsReport {
  items: CashAssetRow[]
  totals: CashAssetsTotals
}

const ENDPOINT = 'reports/accounting/cash-assets'

const http = useHTTP()
const ctx = useContext()
const alertModal = useAlertModal()

const { t } = useI18n({
  messages: {
    en: {
      title: 'Cash Assets',
      selectStore: 'Select Store',
      excel: 'Excel',
      print: 'Print',
      name: 'Name',
      type: 'Type',
      store: 'Store',
      iqd: 'IQD',
      usd: 'USD',
      total: 'Total',
      register: 'Register',
      safe: 'Safe',
      bank: 'Bank'
    },
    ar: {
      title: 'الأصول النقدية',
      selectStore: 'اختر المتجر',
      excel: 'إكسل',
      print: 'طباعة',
      name: 'الاسم',
      type: 'النوع',
      store: 'المتجر',
      iqd: 'IQD',
      usd: 'USD',
      total: 'المجموع',
      register: 'صندوق',
      safe: 'خزنة',
      bank: 'بنك'
    }
  }
})

const locations = ref<Location[]>([])
const locationID = ref<string>('')
const loading = ref(false)
const items = ref<CashAssetRow[]>([])
const totals = ref<CashAssetsTotals>({
  iqdAmount: 0,
  iqdPrecision: 0,
  usdAmount: 0,
  usdPrecision: 2
})

function locationName(loc: Location): string {
  return ctx.locale === 'ar' ? loc.arName || loc.enName : loc.enName || loc.arName
}

function rowName(row: CashAssetRow): string {
  return ctx.locale === 'ar' ? row.arName || row.enName : row.enName || row.arName
}

function rowStore(row: CashAssetRow): string {
  const name = ctx.locale === 'ar' ? row.locationArName || row.locationEnName : row.locationEnName
  return name || '-'
}

function typeLabel(type: string): string {
  if (type === 'register' || type === 'safe' || type === 'bank') {
    return t(type)
  }
  return type
}

function formatAmount(amount: number, precision: number): string {
  // Precision comes from stored transaction data and has occasionally been corrupted
  // (e.g. a stray amount ending up in the precision field). Clamp it to a sane range
  // so a bad row can't blank out the whole report - Intl.NumberFormat throws for
  // maximumFractionDigits outside 0-100, and anything above single digits is never
  // a real currency precision anyway.
  const safePrecision = Number.isFinite(precision) ? Math.min(Math.max(Math.trunc(precision), 0), 6) : 0
  const value = amount / Math.pow(10, safePrecision)
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: safePrecision
  }).format(value)
}

const formattedTotals = computed(() => ({
  iqd: formatAmount(totals.value.iqdAmount, totals.value.iqdPrecision),
  usd: formatAmount(totals.value.usdAmount, totals.value.usdPrecision)
}))

async function load() {
  loading.value = true
  try {
    const params: Record<string, string> = {}
    if (locationID.value) {
      params.location_id = locationID.value
    }
    const report = await http.get<CashAssetsReport>(ENDPOINT, { params })
    items.value = report.items || []
    totals.value = report.totals || {
      iqdAmount: 0,
      iqdPrecision: 0,
      usdAmount: 0,
      usdPrecision: 2
    }
  } catch (err) {
    alertModal.showDanger({ title: (err as { title?: string })?.title ?? 'Error' })
    items.value = []
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  document.title = `${t('title')} - Thecky`
  try {
    const res = await http.get<Location[]>(Location.ENDPOINT)
    locations.value = res.map((l) => Location.from(l))
  } catch {
    locations.value = []
  }
  await load()
})

watch(locationID, load)

function exportExcel() {
  const header = [t('name'), t('type'), t('store'), t('iqd'), t('usd')]
  const rows = items.value.map((row) => [
    rowName(row),
    typeLabel(row.type),
    rowStore(row),
    row.iqdAmount / Math.pow(10, row.iqdPrecision || 0),
    row.usdAmount / Math.pow(10, row.usdPrecision || 0)
  ])
  rows.push([
    t('total'),
    '',
    '',
    totals.value.iqdAmount / Math.pow(10, totals.value.iqdPrecision || 0),
    totals.value.usdAmount / Math.pow(10, totals.value.usdPrecision || 0)
  ])

  const ws = XLSX.utils.aoa_to_sheet([header, ...rows])
  const headerFill = { patternType: 'solid', fgColor: { rgb: 'F3F4F6' } }
  const totalFill = { patternType: 'solid', fgColor: { rgb: 'DBEAFE' } }

  header.forEach((_, col) => {
    const cell = ws[XLSX.utils.encode_cell({ r: 0, c: col })]
    if (cell) cell.s = { font: { bold: true }, fill: headerFill }
  })

  const lastRow = rows.length
  for (let col = 0; col < header.length; col++) {
    const cell = ws[XLSX.utils.encode_cell({ r: lastRow, c: col })]
    if (cell) cell.s = { font: { bold: true }, fill: totalFill }
  }

  for (let r = 1; r <= lastRow; r++) {
    const iqdCell = ws[XLSX.utils.encode_cell({ r, c: 3 })]
    const usdCell = ws[XLSX.utils.encode_cell({ r, c: 4 })]
    if (iqdCell) {
      iqdCell.t = 'n'
      iqdCell.z = '#,##0'
    }
    if (usdCell) {
      usdCell.t = 'n'
      usdCell.z = '#,##0.##'
    }
  }

  ws['!cols'] = [{ wch: 24 }, { wch: 14 }, { wch: 22 }, { wch: 14 }, { wch: 12 }]

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Cash Assets')
  XLSX.writeFile(wb, `cash-assets_${dayjs().format('YYYY-MM-DD')}.xlsx`)
}

function printReport() {
  printJS({
    printable: 'cash-assets-table',
    type: 'html',
    targetStyles: ['*'],
    style: 'table { width: 100%; border-collapse: collapse; } th, td { padding: 8px 12px; text-align: start; } .text-end { text-align: end; }'
  })
}
</script>

<template>
  <div class="m-4" :dir="ctx.getDir()">
    <h1 class="mb-4 text-start text-2xl font-bold text-navy">{{ t('title') }}</h1>

    <div class="mb-4 flex w-full items-center gap-3">
      <el-select
        v-model="locationID"
        :placeholder="t('selectStore')"
        clearable
        filterable
        class="flex-1"
      >
        <el-option
          v-for="loc in locations"
          :key="loc.id"
          :label="locationName(loc)"
          :value="loc.id"
        />
      </el-select>
      <el-button class="w-[100px]" @click="exportExcel">
        <Icon icon="mdi:microsoft-excel" class="me-1" /> {{ t('excel') }}
      </el-button>
      <el-button class="w-[100px] !ml-0" @click="printReport">
        <Icon icon="mdi:printer" class="me-1" /> {{ t('print') }}
      </el-button>
    </div>

    <div v-if="loading" class="flex items-center justify-center py-20">
      <loading-spinner diameter="64px" />
    </div>

    <div v-else id="cash-assets-table" class="overflow-x-auto bg-white">
      <table class="w-full border-collapse text-[15px]">
        <thead>
          <tr class="bg-[#F3F4F6]">
            <th class="px-4 py-3 text-start font-bold text-[#111827]">{{ t('name') }}</th>
            <th class="px-4 py-3 text-start font-bold text-[#111827]">{{ t('type') }}</th>
            <th class="px-4 py-3 text-start font-bold text-[#111827]">{{ t('store') }}</th>
            <th class="px-4 py-3 text-end font-bold text-[#111827]">{{ t('iqd') }}</th>
            <th class="px-4 py-3 text-end font-bold text-[#111827]">{{ t('usd') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in items"
            :key="row.id"
            class="border-b border-[#E5E7EB]"
          >
            <td class="px-4 py-3 text-[#1F2937]">{{ rowName(row) }}</td>
            <td class="px-4 py-3 text-[#1F2937]">{{ typeLabel(row.type) }}</td>
            <td class="px-4 py-3 text-[#1F2937]">{{ rowStore(row) }}</td>
            <td class="px-4 py-3 text-end text-[#1F2937]" dir="ltr">
              {{ formatAmount(row.iqdAmount, row.iqdPrecision) }}
            </td>
            <td class="px-4 py-3 text-end text-[#1F2937]" dir="ltr">
              {{ formatAmount(row.usdAmount, row.usdPrecision) }}
            </td>
          </tr>
          <tr class="bg-[#DBEAFE] font-bold">
            <td class="px-4 py-3">{{ t('total') }}</td>
            <td class="px-4 py-3"></td>
            <td class="px-4 py-3"></td>
            <td class="px-4 py-3 text-end" dir="ltr">{{ formattedTotals.iqd }}</td>
            <td class="px-4 py-3 text-end" dir="ltr">{{ formattedTotals.usd }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
